import React from 'react';

interface FeeConfigurationSkeletonProps {
  /**
   * Which tab is currently active
   */
  activeTab?: 'fees' | 'discounts' | 'taxes';

  /**
   * Number of rows to show in the skeleton table
   */
  rowCount?: number;
}

/**
 * FeeConfigurationSkeleton component
 *
 * This component provides a loading skeleton specifically for the fee configuration table content.
 * It only renders the table skeleton without duplicating headers or tabs.
 */
export const FeeConfigurationSkeleton: React.FC<
  FeeConfigurationSkeletonProps
> = ({ activeTab = 'fees', rowCount = 5 }) => {
  // Fixed width values to prevent hydration errors
  const cellWidths = ['60%', '70%', '80%', '90%', '75%'];

  return (
    <div className="bg-white rounded-xl shadow-sm border border-[#E4E8EA] overflow-hidden">
      {/* Table Header */}
      <div
        className="grid gap-4 p-4 border-b border-[#E4E8EA]"
        style={{
          gridTemplateColumns:
            activeTab === 'fees'
              ? '2fr 1fr 2fr 1fr 80px'
              : '2fr 1fr 1fr 1fr 80px',
        }}
      >
        {[...Array(activeTab === 'fees' ? 5 : 5)].map((_, i) => (
          <div
            key={i}
            className="h-6 bg-gray-200 rounded-md animate-pulse"
            style={{
              width: '90%',
              animationDelay: `${i * 0.05}s`,
            }}
          ></div>
        ))}
      </div>

      {/* Table Rows */}
      {[...Array(rowCount)].map((_, rowIndex) => (
        <div
          key={rowIndex}
          className="border-b border-[#E4E8EA] p-4 last:border-b-0"
        >
          <div
            className="grid gap-4"
            style={{
              gridTemplateColumns:
                activeTab === 'fees'
                  ? '2fr 1fr 2fr 1fr 80px'
                  : '2fr 1fr 1fr 1fr 80px',
            }}
          >
            {[...Array(activeTab === 'fees' ? 5 : 5)].map((_, cellIndex) => (
              <div
                key={cellIndex}
                className={`${cellIndex === 0 ? 'space-y-2' : ''} ${cellIndex === 4 ? 'flex justify-center gap-2' : ''}`}
              >
                {cellIndex === 0 ? (
                  <>
                    <div
                      className="h-6 bg-gray-200 rounded-md animate-pulse"
                      style={{
                        width: '80%',
                        animationDelay: `${rowIndex * 0.05 + cellIndex * 0.01}s`,
                      }}
                    ></div>
                    <div
                      className="h-4 bg-gray-200 rounded-md animate-pulse"
                      style={{
                        width: '60%',
                        animationDelay: `${rowIndex * 0.05 + cellIndex * 0.01 + 0.02}s`,
                      }}
                    ></div>
                  </>
                ) : cellIndex === 4 ? (
                  <>
                    <div className="h-8 w-8 bg-gray-200 rounded-full animate-pulse"></div>
                    <div className="h-8 w-8 bg-gray-200 rounded-full animate-pulse"></div>
                  </>
                ) : cellIndex === 1 ? (
                  <div
                    className="h-6 bg-gray-200 rounded-full animate-pulse"
                    style={{
                      width: '70%',
                      animationDelay: `${rowIndex * 0.05 + cellIndex * 0.01}s`,
                    }}
                  ></div>
                ) : cellIndex === 3 ? (
                  <div
                    className="h-6 bg-gray-200 rounded-full animate-pulse"
                    style={{
                      width: '40%',
                      animationDelay: `${rowIndex * 0.05 + cellIndex * 0.01}s`,
                    }}
                  ></div>
                ) : (
                  <div
                    className="h-6 bg-gray-200 rounded-md animate-pulse"
                    style={{
                      width: cellWidths[cellIndex % cellWidths.length],
                      animationDelay: `${rowIndex * 0.05 + cellIndex * 0.01}s`,
                    }}
                  ></div>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};
